import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { beforeAfterProjects } from '../data/mock';

const BeforeAfterComparison = () => {
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const currentProject = beforeAfterProjects[currentProjectIndex];

  const handleMove = (e) => {
    if (!isDragging && e.type !== 'click') return;
    
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = (e.type.includes('mouse') ? e.clientX : e.touches[0].clientX) - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const nextProject = () => {
    setCurrentProjectIndex((prev) => (prev + 1) % beforeAfterProjects.length);
    setSliderPosition(50); // Reset slider position
  };

  const prevProject = () => {
    setCurrentProjectIndex((prev) => (prev - 1 + beforeAfterProjects.length) % beforeAfterProjects.length);
    setSliderPosition(50); // Reset slider position
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="before-after" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-red-600 text-white mb-4 px-4 py-2 text-sm font-semibold">
            REAL RESULTS
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            See the Difference
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Real Transformation Results
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Interactive Comparison Slider */}
            <div className="relative">
              <div
                ref={containerRef}
                className="relative w-full aspect-square rounded-xl overflow-hidden shadow-2xl cursor-ew-resize select-none"
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMove}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchEnd={handleMouseUp}
                onTouchMove={handleMove}
                onClick={handleMove}
              >
                {/* After Image (Background) */}
                <img
                  src={currentProject.afterImage}
                  alt="After"
                  className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Before Image (Overlay with clip) */}
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                  <img
                    src={currentProject.beforeImage}
                    alt="Before"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>

                {/* Slider Handle */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                  style={{ left: `${sliderPosition}%` }}
                >
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center">
                    <ChevronLeft size={16} className="text-gray-700 absolute left-1" />
                    <ChevronRight size={16} className="text-gray-700 absolute right-1" />
                  </div>
                </div>

                {/* Labels */}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-black/70 text-white backdrop-blur-sm">
                    BEFORE
                  </Badge>
                </div>
                <div className="absolute top-4 right-4">
                  <Badge className="bg-green-600/90 text-white backdrop-blur-sm">
                    AFTER
                  </Badge>
                </div>

                {/* Instruction hint */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-black/70 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
                    ← Drag to compare →
                  </div>
                </div>
              </div>

              {/* Navigation Arrows & Dots */}
              {beforeAfterProjects.length > 1 && (
                <div className="mt-6 flex items-center justify-center gap-4">
                  <button
                    onClick={prevProject}
                    className="bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-colors"
                  >
                    <ChevronLeft size={24} className="text-gray-700" />
                  </button>

                  <div className="flex gap-2">
                    {beforeAfterProjects.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentProjectIndex(index);
                          setSliderPosition(50);
                        }}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === currentProjectIndex
                            ? 'bg-red-600'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={nextProject}
                    className="bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-colors"
                  >
                    <ChevronRight size={24} className="text-gray-700" />
                  </button>
                </div>
              )}

              <p className="text-center text-sm text-gray-500 mt-3">
                Project {currentProjectIndex + 1} of {beforeAfterProjects.length}
              </p>
            </div>

            {/* Details */}
            <div>
              <h3 className="text-3xl font-bold text-black mb-2">
                {currentProject.title}
              </h3>
              <p className="text-lg text-red-600 font-semibold mb-4">
                {currentProject.subtitle}
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {currentProject.description}
              </p>

              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <h4 className="text-xl font-bold text-black mb-4">
                  What We Did:
                </h4>
                <div className="space-y-3">
                  {currentProject.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToContact}
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold"
                >
                  Get Your Free Estimate
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = `tel:425-499-4713`}
                  className="border-red-600 text-red-600 hover:bg-red-50 px-8 py-6 text-lg font-semibold"
                >
                  Call Now
                </Button>
              </div>

              <p className="text-sm text-gray-500 mt-4">
                📍 {currentProject.location}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-white rounded-xl p-8 shadow-lg max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-black mb-4">
            Ready to Transform Your Roof?
          </h3>
          <p className="text-gray-600 mb-6">
            We can deliver the same stunning results for your home. Contact us today for a free consultation!
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold"
          >
            Request Your Free Estimate
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterComparison;
